import React, { useState, useRef } from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import { PlusIcon, CursorArrowRaysIcon, ArrowRightIcon, TrashIcon,
         ArrowDownTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { LLENC } from '../../tema';
import { exportaLlencPDF } from '../../exportaPDF';
import {
  MIDES, OPERATIVA, esMonitoratge, operatives, monitoratge,
  numeroDe, potDependre, avisos, geometriaDependencia, fronteraOperativa, parteixNom,
  esborraActivitat, nouModelConceptual,
} from '../../modelsConceptuals';

// L'ETAPA 4: ELS MODELS CONCEPTUALS.
//
// Fins al 26/08/2026 era un fitxer de vuit línies que deia «encara no». Les regles i la geometria
// són a `src/modelsConceptuals.js`, amb proves pròpies (`npm run prova:conceptuals`); aquí només
// hi ha el dibuix i el ratolí.
//
// ── PER QUÈ NO ÉS UN FORMULARI ───────────────────────────────────────────────────────────────
//
// Un model conceptual és **el graf de les activitats que un sistema hauria de fer** per complir
// el que diu la seva definició arrel, ordenades per **dependència lògica**. La fletxa vol dir
// «això ha d'haver passat perquè allò pugui passar», i **no** influència: per això un cicle aquí
// és una contradicció, mentre que al llenç de bucles causals és justament el que s'hi busca.
//
// I **penja d'UNA definició arrel**. No hi ha «el model conceptual del problema»: n'hi ha un per
// cada manera de mirar-se la situació, i comparar-los és part de la feina.

const ConceptualModelsStage = ({ data, onUpdate }) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const [mode, setMode] = useState('select');
  const [seleccionat, setSeleccionat] = useState(null);
  const [traçant, setTraçant] = useState(null);
  const [avis, setAvis] = useState(null);
  // EL PANELL DE LA DEFINICIÓ ARREL, TANCAT PER DEFECTE.
  //
  // Flotava damunt del llenç a dalt a l'esquerra i **tapava les activitats que hi havia a sota**.
  // El text és útil —s'hi comprova cada activitat— però no s'ha de llegir contínuament: es mira
  // un cop, es pensa, i es torna a dibuixar. Ara s'obre des de la barra, on hi ha el recompte
  // d'avisos perquè es vegi que n'hi ha sense haver-lo d'obrir.
  const [veureRD, setVeureRD] = useState(false);
  const [editant, setEditant] = useState(null);
  const arrossegant = useRef(null);

  const rootDefinitions = data.rootDefinitions || [];
  const models = data.conceptualModels || [];
  const [rdTriada, setRdTriada] = useState(rootDefinitions[0]?.id || null);
  const rd = rootDefinitions.find((x) => x.id === rdTriada) || null;
  const model = models.find((m) => m.rootDefinitionId === rdTriada) || null;

  const desa = (nou) => {
    onUpdate({ conceptualModels: models.some((m) => m.id === nou.id)
      ? models.map((m) => (m.id === nou.id ? nou : m))
      : [...models, nou] });
  };

  const crea = () => desa(nouModelConceptual(rdTriada, Date.now()));

  const nomDe = (a) => (a.clau ? t(`ssm.${a.clau}`) : a.name);
  const cerca = (id) => (model?.activities || []).find((a) => a.id === id);
  const llistaAvisos = model ? avisos(model, rd) : [];

  // ── EL RATOLÍ ──────────────────────────────────────────────────────────────────────────────

  const punt = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const clicaLlenc = (e) => {
    if (editant) { setEditant(null); return; }
    if (traçant) { setTraçant(null); return; }
    if (mode === 'add' && model) {
      const p = punt(e);
      const n = operatives(model).length + 1;
      const nova = { id: `ac_${Date.now()}`, name: t('ssm.cmActivitatN', { n }),
                     type: OPERATIVA, x: p.x, y: p.y };
      desa({ ...model, activities: [...model.activities, nova] });
      setSeleccionat(nova.id);
  // Torna a triar després d'afegir: deixar l'eina posada és el que fa que acabis omplint el llenç
  // sense voler. La intuïció després de col·locar una cosa és mirar-se-la, no col·locar-ne una
  // altra. NO val per al llapis —dibuixar és continu— ni per a esborrar.
      setMode('select');
      return;
    }
    setSeleccionat(null);
  };

  const clicaActivitat = (e, a) => {
    e.stopPropagation();
    if (mode === 'delete') {
      if (esMonitoratge(a)) { setAvis(t('ssm.cmNoEsborrable')); return; }
      desa(esborraActivitat(model, a.id));
      setSeleccionat(null);
      setMode('select');
      return;
    }
    if (mode === 'link') {
      if (!traçant) { setTraçant({ origen: a, x: a.x, y: a.y }); return; }
      const motiu = potDependre(model, traçant.origen.id, a.id);
      if (motiu) { setAvis(t(motiu)); setTraçant(null); return; }
      desa({ ...model, dependencies: [...model.dependencies,
        { id: `d_${Date.now()}`, from: traçant.origen.id, to: a.id }] });
      setTraçant(null);
      setMode('select');
      return;
    }
    setSeleccionat(a.id);
  };

  const comencaArrossegar = (e, a) => {
    if (mode !== 'select') return;
    e.stopPropagation();
    const p = punt(e);
    arrossegant.current = { id: a.id, dx: a.x - p.x, dy: a.y - p.y };
  };

  const mouRatoli = (e) => {
    if (traçant) { setTraçant({ ...traçant, ...punt(e) }); return; }
    if (!arrossegant.current || !model) return;
    const p = punt(e);
    const { id, dx, dy } = arrossegant.current;
    desa({ ...model, activities: model.activities.map((a) =>
      (a.id === id ? { ...a, x: p.x + dx, y: p.y + dy } : a)) });
  };

  // ── DIBUIX ─────────────────────────────────────────────────────────────────────────────────

  const dibuixaDependencia = (d) => {
    const o = cerca(d.from), de = cerca(d.to);
    if (!o || !de) return null;
    const g = geometriaDependencia(o, de);
    const sel = seleccionat === d.id;
    const color = sel ? LLENC.estoc.seleccio : LLENC.linia;
    return (
      <g key={d.id} className="cursor-pointer"
         onClick={(e) => {
           e.stopPropagation();
           if (mode === 'delete') {
             desa({ ...model, dependencies: model.dependencies.filter((x) => x.id !== d.id) });
             setMode('select');
             return;
           }
           setSeleccionat(d.id);
         }}>
        <line x1={g.a.x} y1={g.a.y} x2={g.b.x} y2={g.b.y} stroke={color} strokeWidth={sel ? 2.4 : 1.5} />
        <polygon points="0,0 -10,-4.5 -10,4.5" fill={color}
          transform={`translate(${g.b.x},${g.b.y}) rotate(${g.angle})`} />
        <line x1={g.a.x} y1={g.a.y} x2={g.b.x} y2={g.b.y} stroke="transparent" strokeWidth="14" />
      </g>
    );
  };

  const dibuixaActivitat = (a) => {
    const { amplada, alcada } = MIDES.activitat;
    const sel = seleccionat === a.id;
    const mc = esMonitoratge(a);
    const n = numeroDe(model, a.id);
    const dubtosa = llistaAvisos.some((x) => x.id === a.id);
    return (
      <g key={a.id}
         onClick={(e) => clicaActivitat(e, a)}
         onDoubleClick={(e) => { e.stopPropagation(); if (!mc) setEditant({ id: a.id, valor: a.name }); }}
         onMouseDown={(e) => comencaArrossegar(e, a)}
         className={mode === 'select' ? 'cursor-move' : 'cursor-pointer'}>
        <rect x={a.x - amplada / 2} y={a.y - alcada / 2} width={amplada} height={alcada}
          rx={mc ? 26 : 8}
          fill={mc ? LLENC.flux.farciment : LLENC.estoc.farciment}
          stroke={dubtosa ? LLENC.enCurs : sel ? LLENC.estoc.seleccio : mc ? LLENC.flux.vora : LLENC.estoc.vora}
          strokeWidth={sel || dubtosa ? 3 : 2}
          strokeDasharray={mc ? '6 3' : undefined} />
        {n !== null && (
          <text x={a.x - amplada / 2 + 10} y={a.y - alcada / 2 + 15} fontSize="10" fontWeight="700"
            fill={LLENC.textPal} className="pointer-events-none select-none">{n}</text>
        )}
        {(() => {
          // Partit en línies i no retallat: un model conceptual amb els noms a mitges no es pot
          // llegir, que és justament per al que serveix.
          const linies = parteixNom(nomDe(a));
          const dalt = a.y + 4 - (linies.length - 1) * 6.5;
          return linies.map((l, i) => (
            <text key={i} x={a.x} y={dalt + i * 13} textAnchor="middle" fontSize="11.5"
              fill={LLENC.text} className="pointer-events-none select-none">{l}</text>
          ));
        })()}
      </g>
    );
  };

  const frontera = model ? fronteraOperativa(model) : null;
  const vigilar = model ? monitoratge(model).find((a) => a.id === 'mc_vigilar') : null;
  const accio = model ? monitoratge(model).find((a) => a.id === 'mc_accio') : null;

  return (
    <MarcEtapa propi className="flex-col">
      {/* TOT EL QUE NO ÉS EL LLENÇ VA A DALT.
          Abans hi havia 288 px de columna a l'esquerra amb la tria de definició arrel, el seu
          text i els avisos. Amb els 320 px de la llista d'etapes al costat, **el llenç es
          quedava amb un terç de l'amplada per a un dibuix que és tot horitzontal**: activitats
          en fila i el subsistema de monitoratge a la dreta. La mateixa decisió que als altres
          dos llenços. */}
      <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0">
        {rootDefinitions.length === 0 ? (
          <span className="text-xs text-gray-500">{t('ssm.cmCalRD')}</span>
        ) : (
          <>
            <span className="text-[11px] text-gray-500 mr-1" title={t('ssm.cmDeQuinaWhy')}>
              {t('ssm.cmDeQuina')}
            </span>
            {rootDefinitions.map((x) => {
              const seu = models.find((m) => m.rootDefinitionId === x.id);
              return (
                <button key={x.id} type="button"
                  onClick={() => { setRdTriada(x.id); setSeleccionat(null); }}
                  title={seu ? t('ssm.cmNActivitats', { n: operatives(seu).length }) : t('ssm.cmSenseModel')}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    x.id === rdTriada ? 'bg-primary-700 text-white border-primary-700'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                  {x.systemName || t('ssm.untitled')}
                  <span className={`ml-1.5 ${x.id === rdTriada ? 'text-primary-200' : 'text-gray-400'}`}>
                    {seu ? operatives(seu).length : '·'}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0">
          {[['select', CursorArrowRaysIcon, 'cmModeSelect'],
            ['add', PlusIcon, 'cmModeAdd'],
            ['link', ArrowRightIcon, 'cmModeLink'],
            ['delete', TrashIcon, 'cmModeDelete']].map(([clau, Icona, etq]) => (
            <button key={clau} type="button" onClick={() => { setMode(clau); setTraçant(null); }}
              disabled={!model} title={t(`ssm.${etq}`)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border
                          transition-colors disabled:opacity-40 ${mode === clau
                ? 'bg-primary-700 text-white border-primary-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <Icona className="h-4 w-4" />
              <span className="hidden md:inline">{t(`ssm.${etq}`)}</span>
            </button>
          ))}
          {rd && (
            <button type="button" onClick={() => setVeureRD(!veureRD)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                          border transition-colors ${veureRD
                ? 'bg-primary-700 text-white border-primary-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <InformationCircleIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('ssm.cmVeureRD')}</span>
              {llistaAvisos.length > 0 && (
                <span className={`w-4 h-4 rounded-md text-[10px] font-bold flex items-center
                                  justify-center ${veureRD ? 'bg-white text-primary-700' : 'bg-warn-100 text-warn-700'}`}>
                  {llistaAvisos.length}
                </span>
              )}
            </button>
          )}

          <button type="button" disabled={!model} title={t('tools.exportaPDF')}
            onClick={() => exportaLlencPDF(svgRef.current, rd?.systemName || t('ssm.s4'), t('ssm.s4'))}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                       border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-40">
            <ArrowDownTrayIcon className="h-4 w-4" /><span className="hidden lg:inline">PDF</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden" style={{ background: LLENC.fons }}>
          {/* La definició arrel contra la qual es comprova cada activitat, i els avisos: damunt
              del llenç i en una franja, no en una columna que se'n mengi l'amplada. */}
          {rd && veureRD && (
            <div className="absolute left-3 top-3 z-10 max-w-md bg-white border border-gray-200
                            rounded-md px-3 py-2 shadow-lg">
              <button type="button" onClick={() => setVeureRD(false)}
                className="float-right -mt-0.5 ml-2 text-gray-400 hover:text-gray-600 text-lg leading-none">
                &times;
              </button>
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {t('ssm.cmContraLaRD')}
              </span>
              <p className="text-xs text-gray-700 mt-0.5">
                {rd.rootDefinition || (rd.p && rd.q && rd.r
                  ? t('ssm.cmPQRResum', { p: rd.p, q: rd.q, r: rd.r })
                  : t('ssm.cmRDBuida'))}
              </p>
              {llistaAvisos.length > 0 && (
                <ul className="mt-1.5 pt-1.5 border-t border-gray-100 space-y-1 text-[11px] text-gray-600">
                  {llistaAvisos.map((x, i) => (
                    <li key={i}>· {t(x.clau, { n: x.n, nom: x.id ? nomDe(cerca(x.id)) || '' : '' })}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!model ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-gray-600 max-w-md">
                {rd ? t('ssm.cmComencar') : t('ssm.cmCalRD')}
              </p>
              {rd && (
                <button type="button" onClick={crea}
                  className="px-5 py-2.5 bg-primary-700 text-white rounded-md text-sm font-semibold hover:bg-primary-800">
                  {t('ssm.cmCrear')}
                </button>
              )}
            </div>
          ) : (
            <svg ref={svgRef} className="w-full h-full" onClick={clicaLlenc}
              onMouseMove={mouRatoli} onMouseUp={() => { arrossegant.current = null; }}
              onMouseLeave={() => { arrossegant.current = null; }}>
              {/* El grup marcat, que és el que mira l'exportació a PDF. */}
              <g data-contingut>
                {/* LA FRONTERA DEL SISTEMA OPERATIU. El subsistema de monitoratge s'hi enganxa amb
                    DUES fletxes i no amb dues per activitat: és com ho dibuixa Checkland i és el
                    que fa llegible un model de vuit activitats. */}
                {frontera && (
                  <>
                    <rect x={frontera.x} y={frontera.y} width={frontera.w} height={frontera.h}
                      rx="10" fill="none" stroke={LLENC.linia} strokeWidth="1.5" strokeDasharray="8 5" />
                    <text x={frontera.x + 8} y={frontera.y - 6} fontSize="10" fill={LLENC.textPal}
                      className="select-none">{t('ssm.cmFrontera')}</text>
                    {vigilar && (
                      <line x1={frontera.x + frontera.w} y1={frontera.y + frontera.h / 2}
                        x2={vigilar.x - MIDES.activitat.amplada / 2} y2={vigilar.y}
                        stroke={LLENC.flux.vora} strokeWidth="1.5" strokeDasharray="4 3" />
                    )}
                    {accio && (
                      <line x1={accio.x - MIDES.activitat.amplada / 2} y1={accio.y}
                        x2={frontera.x + frontera.w} y2={frontera.y + frontera.h / 2 + 14}
                        stroke={LLENC.flux.vora} strokeWidth="1.5" strokeDasharray="4 3" />
                    )}
                  </>
                )}
                {(model.dependencies || []).map(dibuixaDependencia)}
                {(model.activities || []).map(dibuixaActivitat)}
                {traçant && (
                  <line x1={traçant.origen.x} y1={traçant.origen.y} x2={traçant.x} y2={traçant.y}
                    stroke={LLENC.enCurs} strokeWidth="2" strokeDasharray="5 4" />
                )}
              </g>
            </svg>
          )}

          {editant && (() => {
            const a = cerca(editant.id);
            if (!a) return null;
            const guarda = () => {
              const nom = editant.valor.trim();
              if (nom) desa({ ...model, activities: model.activities.map((x) =>
                (x.id === a.id ? { ...x, name: nom } : x)) });
              setEditant(null);
            };
            return (
              <input autoFocus value={editant.valor}
                onChange={(e) => setEditant({ ...editant, valor: e.target.value })}
                onBlur={guarda}
                onKeyDown={(e) => { if (e.key === 'Enter') guarda(); if (e.key === 'Escape') setEditant(null); }}
                onClick={(e) => e.stopPropagation()}
                className="absolute z-30 px-2 py-1 text-sm text-center border border-primary-600 rounded-md shadow-sm"
                style={{ left: a.x - 80, top: a.y - 14, width: 160 }} />
            );
          })()}

          {traçant && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md
                            bg-white border border-gray-300 shadow-sm text-xs text-gray-600 z-20">
              {t('ssm.cmTraçant')}
            </div>
          )}
          {avis && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-lg px-4 py-2 rounded-md
                            bg-white border border-warn-400 shadow-sm text-sm text-gray-700"
                 onAnimationEnd={() => setAvis(null)}>
              {avis}
              <button type="button" onClick={() => setAvis(null)}
                className="ml-3 text-gray-400 hover:text-gray-600">&times;</button>
            </div>
          )}
        </div>
      </div>
    </MarcEtapa>
  );
};

export default ConceptualModelsStage;
